import styles from './LoginFormMgm.module.css';

import { useState } from 'react';
import { isMobile } from 'react-device-detect';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import { Button } from '../../../../components/Button';
import { Card } from '../../../../components/Card';
import { FloatVideoCta } from '../../../../components/FloatVideoCta';
import { Input } from '../../../../components/forms/Input';
import { Notify } from '../../../../components/Notify';
import { ModalForgotPassword } from '../ModalForgotPassword';

import { HelmetComponent } from '../../../../components/Helmet';
import * as routes from '../../../../constants/Routes';
import { useAuth } from '../../../../hooks/useAuth';
import { api } from '../../../../server/api';
import { MaskCPF } from '../../../../utils/mask';
import { CaptchaBasic } from '../../../../components/CaptchaBasic';

import { toast } from 'react-toastify';
import { Mark } from '../Mark';
import { FloatingTutorials } from '../../../../components/FloatingTutorials';
import { AiSignup } from '../../../../features/ai-signup';
import { useMediaQuery } from '@mui/material';

interface FormValues {
  cpf?: string;
  email: string;
  password: string;
  captcha: any;
}

interface ILoginFormMgm {
  loginCorretor: boolean;
}

export function LoginFormMgm({ loginCorretor }: ILoginFormMgm) {
  const { client, signIn } = useAuth();
  const navigate = useNavigate();
  const [modalIsOpen, setModalIsOpen] = useState<boolean>(false);
  // Até 800px o botão "Ajuda" sobe para 74px; a isca fica logo acima dele.
  const isNarrowScreen = useMediaQuery('(max-width: 800px)');
  const [isCaptchaResolved, setiSCaptchaResolved] = useState<boolean>(false);

  const {
    register,
    handleSubmit,
    getValues,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>();

  const loginByCpf = async (data: FormValues) => {
    try {
      const parameters = {
        searchFunctionality: 'checkCpfBeforeEmail',
        userType: 'player',
        clientId: client.id,
        cpf: data.cpf,
      };

      const response = await api.post('/search', parameters);

      if (response.data.success) {
        const email = response.data.data[0].email;

        data.email = email;
        await loginByEmail(data);
      } else {
        return Notify(
          `Não encontramos o player com o email inserido!`,
          'error'
        );
      }
    } catch (error) {
      console.log('error loginByCpf', error);
      Notify(`Não encontramos o player com o email inserido!`, 'error');
    }
  };

  const loginByEmail = async (data: FormValues) => {
    try {
      await signIn({
        email: data.email,
        password: data.password,
        clientId: client.id,
      });

      navigate(routes.HOME);
    } catch (error) {
      console.log('onSubmit error', error);

      let errorMsg = '';
      if (error.message === 'auth/wrong-password') {
        errorMsg = 'E-mail ou senha incorretos.';
      } else if (
        error.message === 'auth/account-exists-with-different-credential'
      ) {
        errorMsg = 'Já existe uma conta com o e-mail informado.';
      } else if (
        error.message ===
        'Você não está autorizado a fazer login nesse momento. Contate o administrador para mais informações.'
      ) {
        errorMsg = 'E-mail não cadastrado para este cliente.';
      } else {
        errorMsg = 'E-mail ou senha incorretos.';
      }

      Notify(errorMsg, 'error');
    }
  };

  const handleForm = handleSubmit(async (data) => {
    // permissão para o usuario do teste automatizado não precisar fazer o captcha
    if (isCaptchaResolved || data.email === 'tester@oqdigital.com') {
      if (client.loginEmail === 'false') {
        await loginByCpf(data);
      } else {
        await loginByEmail(data);
      }
    } else {
      toast.warn('Resolva o Captcha!');
    }
  });

  const onHandleRegister = () => {
    navigate(routes.REGISTER);
  };

  const toggleModal = () => {
    setModalIsOpen(!modalIsOpen);
  };

  const renderInactiveClient = () => {
    return (
      <div className={styles.inactiveBox}>
        <i className={`material-icons-outlined ${styles.iconLogo}`}>
          domain_disabled
        </i>
        <p>Cliente inativo</p>
        <a href="https://oqdigital.com" className={styles.btnInactiveOutlined}>
          <i className="material-icons-outlined">arrow_back</i>
          Voltar ao site
        </a>
      </div>
    );
  };

  const renderFormMgm = () => {
    const isLinkRegisterCorretor =
      sessionStorage.getItem('action') &&
      sessionStorage.getItem('clientId') &&
      sessionStorage.getItem('playerId')
        ? true
        : false;

    const handleValidateCaptcha = (result: boolean) => {
      setiSCaptchaResolved(result);
    };

    return (
      <>
        <HelmetComponent
          clientLogoURL={client?.logoURL}
          companyName={client?.companyName}
          clientFavicon={client?.favIcon}
          primaryColor={client?.primaryColor}
          secondaryColor={client?.secondaryColor}
          tabTitle={'Login'}
        />
        <Mark />
        <form onSubmit={handleForm}>
          <div className="m-5">
            <Card isLarge={true} shadow={true}>
              <>
                <p className="text-center fw-semibold text-primary-color">
                  Cadastre-se ou entre agora!
                </p>
                {client.loginEmail === 'false' ? (
                  <>
                    <div className="mb-3">
                      <Input
                        type="text"
                        placeholder="CPF"
                        label="CPF"
                        maxLength={14}
                        errors={errors.cpf ? 'Campo de cpf obrigatório' : ''}
                        {...register('cpf', {
                          required: true,
                          pattern: {
                            value:
                              /([0-9]{3}[.]?[0-9]{3}[.]?[0-9]{3}[-]?[0-9]{2})/i,
                            message: 'CPF inválido.',
                          },
                        })}
                        onChange={(event) => {
                          const { value } = event.target;
                          event.target.value = MaskCPF(value);
                        }}
                        aria-invalid={errors.cpf ? 'true' : 'false'}
                      />
                    </div>
                    <div className="mb-3">
                      <Input
                        type="password"
                        placeholder="Senha"
                        errors={errors.password ? 'Campo obrigatório' : ''}
                        {...register('password', {
                          required: true,
                        })}
                        aria-invalid={errors.password ? 'true' : 'false'}
                      />
                    </div>
                  </>
                ) : (
                  <>
                    <div className="mb-3">
                      <Input
                        type="text"
                        placeholder="E-mail cadastrado"
                        errors={
                          errors.email ? 'Campo de email obrigatório' : ''
                        }
                        {...register('email', {
                          required: true,
                          pattern: {
                            value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                            message: 'invalid email address',
                          },
                        })}
                        aria-invalid={errors.email ? 'true' : 'false'}
                      />
                    </div>
                    <div className="mb-3">
                      <Input
                        type="password"
                        placeholder="Senha"
                        errors={errors.password ? 'Campo obrigatório' : ''}
                        {...register('password', {
                          required: true,
                        })}
                        aria-invalid={errors.password ? 'true' : 'false'}
                      />
                    </div>
                  </>
                )}
                <CaptchaBasic
                  register={register}
                  getValues={getValues}
                  handleValidate={handleValidateCaptcha}
                  result={isCaptchaResolved}
                />
                {errors.captcha && (
                  <span className="tiny-text text-danger">{`${errors.captcha.message}`}</span>
                )}
                <div className="d-grid mt-3">
                  <Button
                    text={isSubmitting ? 'Entrando' : 'Entrar'}
                    icon="person"
                    type="submit"
                    color="primary"
                    isLoading={isSubmitting}
                    disabled={isSubmitting}
                  />
                </div>
                <div className="mt-3">
                  <div className="d-grid">
                    <Button
                      onClick={(e) => {
                        e.preventDefault();
                        toggleModal();
                      }}
                      text="Esqueceu a senha?"
                      icon="password"
                    />
                  </div>
                </div>

                {/* {loginCorretor && <></>} */}

                {(isLinkRegisterCorretor ||
                  (client && client.isActiveRegisterButton === 'true')) && (
                  <>
                    <div className="my-3">
                      <div className="d-grid">
                        <span className="text-center fw-semibold mb-1 tiny-text">
                          É novo por aqui?
                        </span>
                      </div>
                      <div className="d-grid">
                        <Button
                          onClick={onHandleRegister}
                          text="Cadastre-se"
                          icon="person"
                          type="button"
                          color="flat"
                          disabled={isSubmitting}
                        />
                      </div>
                    </div>
                  </>
                )}

                {client &&
                  client.isActiveRegisterButton === 'false' &&
                  !loginCorretor &&
                  !isLinkRegisterCorretor &&
                  client &&
                  client.clientUrl !== 'md' && (
                    <div className={styles.boxMessageUser}>
                      <i className="material-icons-outlined">info</i>
                      <p>
                        Não consegue se cadastrar?
                        <br />
                        <br />
                        Entre em contato com quem enviou o convite para você e
                        solicite o link novamente.
                      </p>
                    </div>
                  )}
              </>
            </Card>
          </div>
        </form>
      </>
    );
  };

  const desktopBackgroundImage =
    loginCorretor && client.backgroundBrokersDesktop
      ? client.backgroundBrokersDesktop
      : client.loginImage;

  const mobileBackgroundImage =
    loginCorretor && client.backgroundBrokersMobile
      ? client.backgroundBrokersMobile
      : client && client.loginImageMobile
        ? client.loginImageMobile
        : client.loginImage;

  return (
    <div
      style={{
        background: isMobile ? '' : `url(${desktopBackgroundImage})`,
      }}
      className={`${styles.contentFull} ${styles.backgroundImage}`}
    >
      {client && client.urlVideoLogin && (
        <FloatVideoCta videoUrl={client.urlVideoLogin} />
      )}
      <div className={styles.contentLeft}>
        {isMobile && <img className="img-fluid" src={mobileBackgroundImage} />}
      </div>
      <div className={styles.contentRight}>
        <div className={styles.centeredAllFlex}>
          {client && client.active ? renderFormMgm() : renderInactiveClient()}
        </div>
      </div>
      <ModalForgotPassword
        clientId={client.id}
        modalIsOpen={modalIsOpen}
        toggleModal={toggleModal}
      />

      <FloatingTutorials
        title="Ajuda"
        subtitle="Veja o(s) vídeo(s) e tire suas dúvidas:"
        links={[
          {
            title: 'Acesso e compartilhamento',
            link: 'https://drive.google.com/file/d/1K4t0qC4L_9YJbg6Ltw1yhDF97B6NaxpJ/preview',
          },
          {
            title: 'Recuperar senha',
            link: 'https://drive.google.com/file/d/1yah6DGiaDe7BIsXlt-uFzTTCK0qNXkxN/preview',
          },
        ]}
      />
      <AiSignup
        offsetY={isNarrowScreen ? 128 : 64}
        onLogin={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
      />
    </div>
  );
}
